import { Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useClinics';
import { pickPostAuthDestination } from '@/hooks/usePostAuthRoute';
import { Redirect } from '@/routing/Redirect';

/**
 * Public route wrapper for login / register. The pages themselves render the
 * full-bleed split-pane layout per the Synapse design; this wrapper only
 * enforces the "already signed in? skip past auth" redirect.
 *
 * That redirect may leave this clinic's URL namespace — a member of `acme` who
 * opens `/beta/login` while signed in belongs at `/acme/`, not `/beta/` — which
 * is why it goes through `Redirect` rather than `<Navigate>`.
 *
 * Note: create-dispensary intentionally lives outside this layout because the
 * user IS authenticated when they hit it (they just haven't joined a clinic).
 * That route handles its own redirect rules.
 */
export function AuthLayout() {
  const { isAuthenticated, memberships } = useAuth();
  // `memberships` is not persisted (authStore partialize), so a signed-in user
  // arriving here on a fresh page load hydrates with an empty list. Deciding
  // before the fetch settles would read that as "you belong to nowhere" and
  // route them to onboarding, only to bounce again a moment later.
  const membershipsQuery = useMyMemberships({ enabled: isAuthenticated });

  if (!isAuthenticated) return <Outlet />;
  const membershipsReady = memberships.length > 0 || membershipsQuery.isFetched;
  if (!membershipsReady) return null;
  return <Redirect to={pickPostAuthDestination(memberships)} />;
}
